import { useState, useEffect, useRef, useMemo } from "react"
import { NavLink, useLocation } from "react-router-dom"
import {
  CaretDown,
  Gear,
  Question,
  SignOut,
  Moon,
  Sun,
  List,
  Buildings,
  SquaresFour,
} from "@phosphor-icons/react"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { useAuthStore, useUIStore } from "../../stores"
import { DivisionSelector } from "./DivisionSelector"

import { ADMIN_PRIVILEGES } from "../../lib/privileges"
import { cn } from "../../lib/utils"
import { keys } from "../../keys"
import { getLogoutUrl } from "../../lib/auth"
import { SECTION_CONFIGS, SIMPLE_PAGES } from "../../lib/navigation"
import type { SectionTab } from "../../lib/navigation"
import { SystemTicketDrawer } from "../../features/system-tickets"

const logoImage = process.env.PUBLIC_URL + "/logo.png"

interface NavItem {
  label: string
  to: string
  privilege: string
  end?: boolean
}

const BASE_NAV_ITEMS: NavItem[] = [
  // { label: "Overview", to: "/", privilege: ADMIN_PRIVILEGES.OVERVIEW, end: true },
    { label: "System", to: "/system", privilege: ADMIN_PRIVILEGES.SYSTEM },
  { label: "Finances", to: "/finances", privilege: ADMIN_PRIVILEGES.FINANCES },

  { label: "Marketing", to: "/marketing", privilege: ADMIN_PRIVILEGES.MARKETING },
  { label: "Analyze", to: "/analyze", privilege: ADMIN_PRIVILEGES.ANALYZE },
  { label: "Communicate", to: "/communication", privilege: ADMIN_PRIVILEGES.COMMUNICATE },
  { label: "Automate", to: "/automations", privilege: ADMIN_PRIVILEGES.AUTOMATE },
  { label: "Training", to: "/training", privilege: ADMIN_PRIVILEGES.TRAINING },

  { label: "Bulk Actions", to: "/bulk", privilege: ADMIN_PRIVILEGES.SYSTEM },
  { label: "Layout", to: "/layout", privilege: ADMIN_PRIVILEGES.LAYOUT },
  { label: "AI", to: "/ai", privilege: ADMIN_PRIVILEGES.AI },
]

function Breadcrumbs() {
  const { pathname } = useLocation()
  const firstSegment = pathname.split("/").filter(Boolean)[0] || ""

  // Section with sub-tabs (Marketing, System, etc.)
  const section = SECTION_CONFIGS[firstSegment]
  if (section) {
    const activeTab = section.tabs.reduce<SectionTab | null>((best, tab) => {
      if (tab.end) return pathname === tab.to ? tab : best
      if (pathname === tab.to || pathname.startsWith(tab.to + "/")) {
        if (!best || tab.to.length > best.to.length) return tab
        return best
      }
      return best
    }, null)

    return (
      <div className="hidden lg:flex items-center text-sm">
        <span className="text-muted-foreground/40 mx-2">/</span>
        <span className="text-muted-foreground">{section.label}</span>
        {activeTab && (
          <>
            <span className="text-muted-foreground/40 mx-2">/</span>
            <span className="text-foreground font-medium">{activeTab.label}</span>
          </>
        )}
      </div>
    )
  }

  // Simple page (no sub-tabs)
  const simplePage = SIMPLE_PAGES[firstSegment]
  if (simplePage) {
    return (
      <div className="hidden lg:flex items-center text-sm">
        <span className="text-muted-foreground/40 mx-2">/</span>
        <span className="text-foreground font-medium">{simplePage}</span>
      </div>
    )
  }

  return null
}

export function Header() {
  const { user } = useAuthStore()
  const { theme, setTheme, domains } = useUIStore()

  const { pathname } = useLocation()
  const isCompanyMode = pathname.startsWith("/company")

  const appUrl = domains?.APP || keys.APP_URL

  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [ticketDrawerOpen, setTicketDrawerOpen] = useState(false)

  const userMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleLogout = () => {
    setUserMenuOpen(false)
    window.location.href = getLogoutUrl()
  }

  const handleSettingsClick = () => {
    setUserMenuOpen(false)
    window.open(`${appUrl}/settings`, "_blank")
  }

  const handleHelpClick = () => {
    setUserMenuOpen(false)
    setTicketDrawerOpen(true)
  }

  const initials = user
    ? `${user.given_name?.[0] || ""}${user.family_name?.[0] || ""}`
    : "U"

  const displayName = user
    ? `${user.given_name || ""} ${user.family_name || ""}`.trim()
    : "User"

  const isOnline = true // TODO: Wire to actual online status

  const navItems = useMemo(() => {
    const privileges = user?.privileges || []
    return BASE_NAV_ITEMS.filter(item => privileges.includes(item.privilege))
  }, [user?.privileges])

  return (
    <div
      className={cn(
        "relative flex flex-col z-50",
        "bg-background/95 backdrop-blur-sm",
        "border-b border-border/80",
        "shadow-[0_1px_3px_0_rgba(0,0,0,0.02)]"
      )}
    >
      {/* Row 1: Top bar - Logo + Division + User */}
      <header className="flex h-14 items-center justify-between px-5 gap-2 lg:gap-4">
        {/* Left: Logo + mobile hamburger */}
        <div className="flex items-center gap-3">
          {/* Mobile: Hamburger menu for nav (hidden in company mode) */}
          {!isCompanyMode && (
            <button
              className="flex items-center justify-center h-10 w-10 -ml-2 lg:hidden rounded-lg hover:bg-accent transition-colors"
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              aria-label="Toggle navigation"
            >
              <List className="h-5 w-5 text-muted-foreground" />
            </button>
          )}

          {/* Logo + ADMIN */}
          <div className="flex items-center gap-2.5">
            <img src={logoImage} alt="IntuiDesk" className="h-7 w-7" />
            <span className="text-sm font-bold tracking-wider text-foreground">
              ADMIN
            </span>
          </div>

          {/* Breadcrumbs */}
          <Breadcrumbs />
        </div>

        {/* Right: Company + Division + User */}
        <div className="flex items-center gap-1 lg:gap-2">
          <NavLink
            to="/"
            title="Back to Dashboard"
            className={cn(
              "flex items-center justify-center h-9 w-9 rounded-lg transition-colors",
              !isCompanyMode
                ? "bg-accent text-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <SquaresFour className="h-4 w-4" />
          </NavLink>
          {user?.privileges?.includes(ADMIN_PRIVILEGES.COMPANY) && (
            <NavLink
              to="/company"
              title="Company Settings"
              className={cn(
                "flex items-center justify-center h-9 w-9 rounded-lg transition-colors",
                isCompanyMode
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <Buildings className="h-4 w-4" />
            </NavLink>
          )}
          <DivisionSelector />

          {/* User menu */}
          <div ref={userMenuRef} className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={cn(
                "flex items-center gap-2.5 rounded-lg px-2 py-1.5",
                "transition-all duration-200",
                userMenuOpen ? "bg-accent shadow-inner" : "hover:bg-muted"
              )}
            >
              <div className="relative">
                <Avatar
                  className={cn(
                    "h-8 w-8 ring-2 ring-offset-1 transition-all duration-200",
                    isOnline ? "ring-success" : "ring-border"
                  )}
                >
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="text-sm bg-primary text-white font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span
                  className={cn(
                    "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background",
                    isOnline ? "bg-success shadow-sm shadow-success/50" : "bg-muted-foreground"
                  )}
                />
              </div>
              <span className="hidden text-sm font-medium text-foreground lg:inline">
                {displayName}
              </span>
              <CaretDown
                className={cn(
                  "h-3 w-3 text-muted-foreground hidden lg:block transition-transform duration-200",
                  userMenuOpen && "rotate-180"
                )}
              />
            </button>

            {/* User dropdown menu */}
            {userMenuOpen && (
              <div
                className={cn(
                  "absolute right-0 top-full mt-2 w-64 rounded-xl",
                  "bg-background border border-border shadow-xl",
                  "overflow-hidden z-50",
                  "animate-flyout-enter"
                )}
              >
                <div className="px-4 py-3 bg-gradient-to-br from-muted to-background border-b border-border">
                  <p className="text-sm font-semibold text-foreground">{displayName}</p>
                  <p className="text-xs text-muted-foreground">{user?.email || "user@example.com"}</p>
                  <div
                    className={cn(
                      "flex items-center gap-1.5 mt-2 text-xs",
                      isOnline ? "text-success" : "text-muted-foreground"
                    )}
                  >
                    <span
                      className={cn(
                        "h-2 w-2 rounded-full",
                        isOnline ? "bg-success" : "bg-muted-foreground"
                      )}
                    />
                    <span>{isOnline ? "Online" : "Offline"}</span>
                  </div>
                </div>

                <div className="py-1">
                  <button
                    onClick={handleSettingsClick}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Gear className="h-4 w-4 text-muted-foreground" />
                    <span>Settings</span>
                  </button>
                  <button
                    onClick={() => {
                      const newTheme = theme === "light" ? "dark" : "light"
                      setTheme(newTheme)
                    }}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    {theme === "light" ? (
                      <Moon className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Sun className="h-4 w-4 text-yellow-400" />
                    )}
                    <span>Theme</span>
                    <span className="ml-auto text-xs text-muted-foreground capitalize">{theme}</span>
                  </button>
                </div>

                <div className="border-t border-border py-1">
                  <button
                    onClick={handleHelpClick}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Question className="h-4 w-4 text-muted-foreground" />
                    <span>Help & Support</span>
                  </button>
                </div>

                <div className="border-t border-border py-1">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-danger hover:bg-danger/5 transition-colors"
                  >
                    <SignOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Row 2: Main navigation bar (hidden in company mode) */}
      {!isCompanyMode && (
      <nav
        className={cn(
          "border-t border-border/50 px-3 overflow-x-auto scrollbar-hide",
          // Mobile: toggle visibility
          mobileNavOpen ? "block" : "hidden lg:block"
        )}
      >
        <div className="flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileNavOpen(false)}
              className={({ isActive }) =>
                cn(
                  "relative px-3 py-2.5 text-[13px] font-medium whitespace-nowrap transition-colors duration-150",
                  isActive
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-primary" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
      )}

      <SystemTicketDrawer open={ticketDrawerOpen} onOpenChange={setTicketDrawerOpen} />
    </div>
  )
}
